import React, { useContext, useEffect, useState } from 'react'
import { Divider, ExpandableListItem, List, Separator } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import { TreeLink } from '../../app/Links'
import ObjectView from '../../common/ObjectView'
import { UserContext } from '../UserContext'
import useNotifications from '../../../hooks/useNotifications'
import { getPersonCount } from '../../../services/treeService'
import { usePageTitle } from '../../app/PageTitleContext'

async function enrichWithPersonCount(trees, onError, t) {
  return Promise.all(
    trees.map(async (tree) => {
      const personCount = await getPersonCount(tree.tree.treeId).catch((err) =>
        onError(
          t('tree.error.personCount', 'Error getting persons count for tree {treeId}: {message}', {
            treeId: tree.tree.treeId,
            message: err.message,
          })
        )
      )
      return { ...tree, tree: { ...tree.tree, personCount: personCount?.data } }
    })
  )
}

export default function UserOwnedTrees() {
  usePageView({ page_detail: 'DMConsole:UserTrees' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.user.trees'))
  const { ownedTrees: initialOwnedTrees, participantTrees: initialParticipantTrees } = useContext(UserContext)
  const [ownedTrees, setOwnedTrees] = useState([])
  const [participantTrees, setParticipantTrees] = useState([])
  const { error } = useNotifications()

  useEffect(() => {
    if (initialOwnedTrees?.length) enrichWithPersonCount(initialOwnedTrees, error, t).then(setOwnedTrees)
    if (initialParticipantTrees?.length)
      enrichWithPersonCount(initialParticipantTrees, error, t).then(setParticipantTrees)
  }, [initialOwnedTrees, initialParticipantTrees, error, t])

  return (
    <>
      {ownedTrees.length > 0 && (
        <>
          <span>{t('user.trees.ownedTrees', 'Trees Owned by User')}</span>
          <Divider />
          <List id="userOwnedTreesList">
            {ownedTrees?.map((ownedTree) => (
              <React.Fragment key={ownedTree.tree.treeId}>
                <span>
                  <ExpandableListItem
                    id={`listitem - ${ownedTree.tree.treeId}`}
                    primaryText={
                      <TreeLink
                        treeId={ownedTree.tree.treeId}
                        treeName={ownedTree.tree.name}
                        treeType={ownedTree.treeType}
                      />
                    }
                    secondaryText={ownedTree.tree.tombstoned ? t('tombstoned', 'Tombstoned') : null}
                    selected={ownedTree.tree.tombstoned}
                  >
                    <ObjectView object={ownedTree.tree} fieldsToOmit={['devkeys']} />
                  </ExpandableListItem>
                </span>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </>
      )}
      {participantTrees.length > 0 && (
        <>
          <Separator size="lg" />
          <span>{t('user.trees.participantTrees', 'Trees User Participates In')}</span>
          <Divider />
          <List id="userParticipantTreesList">
            {participantTrees?.map((participantTree) => (
              <React.Fragment key={participantTree.tree.treeId}>
                <span>
                  <ExpandableListItem
                    id={`listitem - ${participantTree.tree.treeId}`}
                    primaryText={
                      <TreeLink
                        treeId={participantTree.tree.treeId}
                        treeName={participantTree.tree.name}
                        treeType={participantTree.treeType}
                      />
                    }
                    secondaryText={participantTree.tree.tombstoned ? t('tombstoned', 'Tombstoned') : null}
                    selected={participantTree.tree.tombstoned}
                  >
                    <ObjectView object={participantTree.tree} fieldsToOmit={['devkeys']} />
                  </ExpandableListItem>
                </span>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </>
      )}
    </>
  )
}
